/**
 * DetailSection Component
 * 
 * A reusable React component that creates a standardized section layout for displaying
 * accommodation details. It provides a consistent structure with a title and content area.
 * 
 * Features:
 * - Consistent styling and layout for detail sections
 * - Flexible content area using children prop
 * - Customizable styling through className prop
 * - Clear visual hierarchy with separated title and content areas
 * 
 * Usage Example:
 * ```jsx
 * <DetailSection title="Location Details" className="location-section">
 *   <p>Address information here...</p>
 * </DetailSection>
 * ```
 * 
 * Styling:
 * - Uses 'detail-section' as base class
 * - Separates content into 'section-title' and 'section-content' areas
 * - Supports additional styling through className prop
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - The title to display at the top of the section
 * @param {React.ReactNode} props.children - The content to display in the section body
 * @param {string} [props.className=''] - Additional CSS class names to apply to the root element
 * 
 * @returns {React.ReactElement} A structured section with title and content areas
 */

import React from 'react';

const DetailSection = ({ title, children, className = '' }) => {
  return (
    <div className={`detail-section ${className}`}>
      {/* Title area - Displays the section heading */}
      <div className="section-title">
        {title}
      </div>
            
      {/* Content area - Renders the child components/elements */}
      <div className="section-content">
        {children}
      </div>
    </div>
  );
};

export default DetailSection; 